import isPropValid from '@emotion/is-prop-valid'
import { LanguageSwitcher, ILanguageSwitcherLanguage, LinkButton } from '@hg/stylizator/components'
import { Text } from '@hg/stylizator/typography'
import { Flex, themeColors } from '@hg/stylizator/utils'
import classNames from 'classnames'
import * as React from 'react'
import styled from 'styled-components'

import ClientOnly from '../ClientOnly'

import ActiveMark from 'app/components/Header/ActiveMark'
import Burger from 'app/components/Header/Burger'
import MobileMenu from 'app/components/Header/MobileMenu'
import { IMenuItem, MOBILE_HEADER_MIN_WIDTH } from 'app/components/Header/model'

interface IProps {
  opened?: boolean
  onMenuToggle?: () => void
  items: IMenuItem[]
  scrolled?: boolean
  languages: ILanguageSwitcherLanguage[]
  logo: React.ReactElement
  logoUrl?: string
}

const renderMenuItem = (item: IMenuItem, index?: number) => {
  if (item.classes?.includes('menu-item-primary-small')) {
    return (
      <MenuButton
        className={classNames(item.classes)}
        type="primary"
        size="small"
        key={item.url + index}
        href={item.url}
        target={item.target}
      >
        {item.name}
      </MenuButton>
    )
  }

  if (item.classes?.includes('menu-item-secondary-small')) {
    return (
      <MenuButton
        className={classNames(item.classes)}
        type="secondary"
        size="small"
        key={item.url + index}
        href={item.url}
        target={item.target}
      >
        {item.name}
      </MenuButton>
    )
  }

  return (
    <Text key={item.url + index} type="label">
      <a href={item.url} target={item.target} className={classNames('menu-item', item.classes ?? [])}>
        <span className="menu-item-label">{item.name}</span>
        {item.active && <ActiveMark />}
      </a>
    </Text>
  )
}

interface Menus {
  left: IMenuItem[]
  right: IMenuItem[]
}

const Header: React.FC<IProps> = (props) => {
  const menus = props.items.reduce<Menus>(
    (menus, item) => {
      const isRightMenuItem = item.classes?.includes('right-menu-item')
      menus[isRightMenuItem ? 'right' : 'left'].push(item)
      return menus
    },
    { left: [], right: [] },
  )

  const mobileExtraMenu = props.items.filter((item) => item.classes?.includes('mobile-extra-menu-item'))

  const selectedLanguage = props.languages?.find((lang) => lang.isSelected)

  return (
    <>
      <WrapperView scrolled={props.scrolled}>
        <MenuWrapper>
          <LogoLinkView href={props.logoUrl ? props.logoUrl : '/'} rel="noopener">
            <LogoView>{props.logo}</LogoView>
          </LogoLinkView>
          <NavView>
            {menus.left.map(renderMenuItem)}
            {selectedLanguage && (
              <>
                <SeparatorWrap>
                  <Separator />
                </SeparatorWrap>
                <LanguageSwitcher
                  mode="dropdown"
                  languages={props.languages}
                  selectedLanguage={{
                    abbreviation: selectedLanguage.abbreviation,
                    label: selectedLanguage.abbreviation,
                    isSelected: true,
                  }}
                />
              </>
            )}
            {menus.right.map(renderMenuItem)}
          </NavView>
          <ClientOnly>
            {mobileExtraMenu.length > 0 && !props.opened && (
              <MobileNavView>{mobileExtraMenu.map(renderMenuItem)}</MobileNavView>
            )}
          </ClientOnly>
          <ClientOnly>
            {props.onMenuToggle && <Burger opened={Boolean(props.opened)} onClick={props.onMenuToggle} />}
          </ClientOnly>
        </MenuWrapper>
      </WrapperView>
      <ClientOnly>
        {props.items.length > 0 && (
          <MobileMenu items={props.items} opened={props.opened} languages={props.languages} />
        )}
      </ClientOnly>
    </>
  )
}

const SeparatorWrap = styled(Flex)`
  padding: 0 32px;
  align-items: center;
`

const Separator = styled.div`
  height: 32px;
  width: 1px;
  background-color: ${themeColors.gray['30']};
`

const NavView = styled.nav`
  display: flex;
  align-items: center;

  a,
  button {
    margin-left: 32px;
  }

  a {
    &:hover .menu-item-label {
      color: ${themeColors.gray['60']};
    }

    &:active .menu-item-label {
      color: ${themeColors.gray['100']};
    }
  }

  @media (max-width: ${MOBILE_HEADER_MIN_WIDTH}px) {
    display: none;
  }
`

const MenuButton = styled(LinkButton)`
  width: auto;
  padding: 0 8px;

  & + & {
    margin-left: 16px;
  }
`

const MobileNavView = styled.nav`
  display: none;

  @media (max-width: ${MOBILE_HEADER_MIN_WIDTH}px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    padding-right: 16px;
  }
`

const MenuWrapper = styled(Flex)`
  max-width: 1128px;
  padding: 0 16px;
  margin: 0 auto;
  height: 64px;
  justify-content: space-between;
`

const LogoLinkView = styled.a`
  display: flex;
  flex: 1;
  justify-content: flex-start;
  align-items: center;
`

const LogoView = styled.div`
  width: 195px;

  @media (max-width: ${MOBILE_HEADER_MIN_WIDTH}px) {
    width: 140px;
  }
`

interface WrapperProps {
  scrolled?: boolean
}

const WrapperView = styled.div.withConfig({ shouldForwardProp: isPropValid })<WrapperProps>`
  position: sticky;
  top: 0;
  width: 100%;
  background-color: #fff;
  z-index: 1000;
  ${(props) =>
    props.scrolled && `box-shadow: 0px 4px 4px rgba(35, 68, 101, 0.1), 0px 4px 16px rgba(35, 68, 101, 0.1);`}
`

export default Header
